import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { randomBytes } from "crypto"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Get the current URL dynamically
export function getCurrentUrl(): string {
  if (typeof window !== 'undefined') {
    // Client-side: use window.location
    return window.location.origin
  } else {
    // Server-side: use environment variable or default
    return process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000'
  }
}

// Get the current URL with path
export function getCurrentUrlWithPath(path: string = ''): string {
  const baseUrl = getCurrentUrl()
  return `${baseUrl}${path}`
}

/**
 * Serialize Prisma Decimal objects to numbers for client components
 * This prevents "Only plain objects can be passed to Client Components" errors
 */
export function serializeDecimal<T extends Record<string, any>>(obj: T): T {
  if (!obj || typeof obj !== 'object') return obj;
  
  const serialized = { ...obj } as any;
  
  for (const [key, value] of Object.entries(serialized)) {
    // Preserve Date objects - don't serialize them
    if (value instanceof Date) {
      serialized[key] = value;
    } else if (value && typeof value === 'object' && value.constructor && value.constructor.name === 'Decimal') {
      serialized[key] = convertDecimal(value) || 0;
    } else if (value && typeof value === 'object' && ((value as any).s !== undefined && (value as any).e !== undefined && (value as any).d !== undefined)) {
      // Handle Prisma Decimal objects that have been serialized
      serialized[key] = convertDecimal(value) || 0;
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      serialized[key] = serializeDecimal(value);
    } else if (Array.isArray(value)) {
      serialized[key] = value.map((item: any) => 
        typeof item === 'object' ? serializeDecimal(item) : item
      );
    } else if (typeof value === 'string' && !isNaN(Number(value)) && value.includes('.')) {
      serialized[key] = Number(value);
    }
  }
  
  return serialized as T;
}

/**
 * Serialize an array of objects with Decimal fields
 */
export function serializeDecimalArray<T extends Record<string, any>>(arr: T[]): T[] {
  return arr.map(item => serializeDecimal(item));
}

/**
 * Convert Prisma Decimal to number
 * Handles the internal structure of Prisma Decimal objects
 */
export function convertDecimal(decimal: any): number | null {
  if (!decimal) return null
  
  // If it's already a number, return it
  if (typeof decimal === 'number') return decimal
  
  // If it's a string that represents a number, convert it
  if (typeof decimal === 'string') {
    const num = Number(decimal)
    return isNaN(num) ? null : num
  }
  
  // If it's not an object, return null
  if (typeof decimal !== 'object') return null
  
  // Handle Prisma Decimal objects with d, e, s properties
  if (decimal.d && Array.isArray(decimal.d) && typeof decimal.e === 'number' && typeof decimal.s === 'number') {
    try {
      // For Prisma Decimal: value = d[0] * 10^(e - d.length + 1)
      const digits = decimal.d[0]
      const exponent = decimal.e
      const digitCount = digits.toString().length
      const value = digits * Math.pow(10, exponent - digitCount + 1)
      return decimal.s === 1 ? value : -value
    } catch (error) {
      console.warn('Error converting Decimal object:', error, decimal)
      return null
    }
  }
  
  // Try to use toString() method if available (Prisma Decimal has this)
  if (typeof decimal.toString === 'function') {
    try {
      const stringValue = decimal.toString()
      const num = Number(stringValue)
      return isNaN(num) ? null : num
    } catch (error) {
      console.warn('Error converting Decimal via toString:', error, decimal)
      return null
    }
  }
  
  // Try to use toNumber() method if available (Prisma Decimal has this)
  if (typeof decimal.toNumber === 'function') {
    try {
      return decimal.toNumber()
    } catch (error) {
      console.warn('Error converting Decimal via toNumber:', error, decimal)
      return null
    }
  }
  
  return null
}

/**
 * Helper function to safely parse JSON fields (images, tags, etc.)
 * Handles both string and already-parsed array formats
 */
export function parseJsonField<T = any>(field: any, defaultValue: T = null as T): T {
  if (!field) return defaultValue
  if (Array.isArray(field)) return field as T
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field)
      return parsed !== null ? parsed : defaultValue
    } catch {
      return defaultValue
    }
  }
  return field
}

/**
 * Safely format a currency value to 2 decimal places
 * Handles cases where the value might be a string, Decimal object, or undefined
 */
export function formatCurrency(amount: any, currencySymbol: string = '₹', options?: { decimals?: number, forceINR?: boolean }): string {
  const numericAmount = Number(amount || 0);
  if (isNaN(numericAmount)) {
    return `${currencySymbol}0.00`;
  }
  // If options.forceINR, always use Indian format
  if (options?.forceINR) {
    return formatINRCurrency(numericAmount, currencySymbol);
  }
  // If decimals is 0, use Indian format
  if (options?.decimals === 0) {
    return formatINRCurrency(numericAmount, currencySymbol);
  }
  // Default: 2 decimals (for cart/checkout)
  return `${currencySymbol}${numericAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Advanced currency formatting with locale support
 * @param amount - The amount to format
 * @param currencySymbol - The currency symbol (e.g., '₹', '$', '€')
 * @param locale - The locale string (e.g., 'en-IN', 'en-US', 'en-GB')
 * @param currencyCode - The currency code (e.g., 'INR', 'USD', 'EUR') - optional for Intl.NumberFormat
 */
export function formatCurrencyAdvanced(
  amount: any, 
  currencySymbol: string = '₹', 
  locale: string = 'en-IN',
  currencyCode?: string
): string {
  const numericAmount = Number(amount || 0);
  if (isNaN(numericAmount)) {
    return `${currencySymbol}0.00`;
  }

  try {
    // If we have a currency code, use Intl.NumberFormat for proper formatting
    if (currencyCode) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numericAmount);
    }
    
    // Otherwise, use manual formatting with locale-specific number formatting
    const formattedNumber = numericAmount.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return `${currencySymbol}${formattedNumber}`;
  } catch (error) {
    // Fallback to simple formatting if locale is not supported
    return `${currencySymbol}${numericAmount.toFixed(2)}`;
  }
}

/**
 * Safely format a number to a specified number of decimal places
 * Handles cases where the value might be a string, Decimal object, or undefined
 */
export function formatNumber(value: any, decimalPlaces: number = 2): string {
  const numericValue = Number(value || 0);
  if (isNaN(numericValue)) {
    return `0.${'0'.repeat(decimalPlaces)}`;
  }
  return numericValue.toFixed(decimalPlaces);
}

/**
 * Safely format a date with proper error handling
 * @param dateString - The date string to format
 * @param options - Formatting options
 * @returns Formatted date string or 'Invalid Date' if error
 */
export function formatDate(
  dateString: any, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }
): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString('en-US', options);
  } catch (error) {
    return 'Invalid Date';
  }
}

/**
 * Format a date for display in tables (compact format)
 */
export function formatTableDate(dateString: any): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Format a date for detailed display (full format)
 */
export function formatDetailedDate(dateString: any): string {
  return formatDate(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
}

/**
 * Generate a random alphanumeric password
 * @param length - The length of the password (default: 8)
 * @returns A random alphanumeric string
 */
export function generatePassword(length: number = 8): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = randomBytes(length);
  let password = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = bytes[i] % charset.length;
    password += charset[randomIndex];
  }

  return password;
}

/**
 * Phone number utilities for payment gateways
 */

// Common country codes for phone numbers
export const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+1', country: 'US', name: 'United States' },
  { code: '+1', country: 'CA', name: 'Canada' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+86', country: 'CN', name: 'China' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
]

/**
 * Format phone number for payment gateways (RazorPay, etc.)
 * @param phone - The phone number without country code
 * @param countryCode - The country code (e.g., '+91')
 * @returns Formatted phone number for payment gateways
 */
export function formatPhoneForPayment(phone?: string, countryCode?: string): string {
  if (!phone) return '9999999999' // Default fallback
  
  // Remove any existing country code, spaces, dashes, parentheses
  const cleanPhone = phone.replace(/[\s\-\(\)\+]/g, '').replace(/^91/, '').replace(/^0/, '')
  
  // Use provided country code or default to +91 for India
  const code = countryCode || '+91'
  
  // For India (+91), ensure 10 digits
  if (code === '+91') {
    if (cleanPhone.length === 10 && /^\d{10}$/.test(cleanPhone)) {
      return `${code}${cleanPhone}`
    }
  }
  
  // For other countries, basic validation
  if (cleanPhone.length >= 7 && /^\d+$/.test(cleanPhone)) {
    return `${code}${cleanPhone}`
  }
  
  // Return fallback if validation fails
  return '9999999999'
}

/**
 * Validate phone number format
 * @param phone - The phone number to validate
 * @param countryCode - The country code
 * @returns true if valid, false otherwise
 */
export function validatePhoneNumber(phone?: string, countryCode?: string): boolean {
  if (!phone) return false
  
  const cleanPhone = phone.replace(/[\s\-\(\)\+]/g, '').replace(/^91/, '').replace(/^0/, '')
  const code = countryCode || '+91'
  
  if (code === '+91') {
    return cleanPhone.length === 10 && /^\d{10}$/.test(cleanPhone)
  }
  
  // Basic validation for other countries
  return cleanPhone.length >= 7 && cleanPhone.length <= 15 && /^\d+$/.test(cleanPhone)
}

/**
 * Get country code from country ISO code
 * @param countryIso - Country ISO code (e.g., 'IN', 'US')
 * @returns Country code (e.g., '+91', '+1')
 */
export function getCountryCodeFromIso(countryIso: string): string {
  const country = COUNTRY_CODES.find(c => c.country === countryIso)
  return country?.code || '+91'
}

/**
 * Parse phone number with country code
 * @param fullPhone - Full phone number with country code (e.g., '+919876543210')
 * @returns Object with country code and phone number
 */
export function parsePhoneNumber(fullPhone?: string): { countryCode: string; phone: string } {
  if (!fullPhone) return { countryCode: '+91', phone: '' }
  
  // Find matching country code
  const matchingCode = COUNTRY_CODES.find(c => fullPhone.startsWith(c.code))
  
  if (matchingCode) {
    return {
      countryCode: matchingCode.code,
      phone: fullPhone.substring(matchingCode.code.length)
    }
  }
  
  // Default to India if no match
  return { countryCode: '+91', phone: fullPhone.replace(/^\+/, '') }
}

/**
 * Format phone number for display with country code
 * @param phone - The phone number without country code
 * @param phoneCountryCode - The country code (e.g., '+91')
 * @returns Formatted phone number for display (e.g., '+91 98765 43210')
 */
export function formatPhoneForDisplay(phone?: string, phoneCountryCode?: string): string {
  if (!phone) return ''
  
  const countryCode = phoneCountryCode || '+91'
  
  // If phone already includes country code, return as is
  if (phone.startsWith('+')) {
    return phone
  }
  
  // Format the phone number with spaces for better readability
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '')
  
  // Add spacing for common formats
  let formattedPhone = cleanPhone
  if (countryCode === '+91' && cleanPhone.length === 10) {
    // Format Indian numbers as: +91 98765 43210
    formattedPhone = `${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}`
  } else if (countryCode === '+1' && cleanPhone.length === 10) {
    // Format US/Canada numbers as: +1 (555) 123-4567
    formattedPhone = `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`
  } else if (cleanPhone.length >= 7) {
    // Generic formatting for other countries
    const mid = Math.floor(cleanPhone.length / 2)
    formattedPhone = `${cleanPhone.slice(0, mid)} ${cleanPhone.slice(mid)}`
  }
  
  return `${countryCode} ${formattedPhone}`
}

/**
 * Tax calculation utilities for inclusive tax system
 */

export interface TaxCalculation {
  totalAmount: number
  taxableAmount: number
  taxAmount: number
  taxRate: number
  taxName: string
}

/**
 * Calculate inclusive tax breakdown
 * For inclusive tax, the final price includes tax, so we reverse engineer to find the tax amount
 * Formula: Tax Amount = (Total Amount × Tax Rate) / (1 + Tax Rate)
 * Formula: Taxable Amount = Total Amount - Tax Amount
 * 
 * @param totalAmount - The final amount including tax
 * @param taxRate - Tax rate as decimal (e.g., 0.18 for 18%)
 * @param taxName - Name of the tax (e.g., 'GST', 'VAT')
 * @returns Tax calculation breakdown
 */
export function calculateInclusiveTax(
  totalAmount: number, 
  taxRate: number, 
  taxName: string = 'Tax'
): TaxCalculation {
  if (taxRate <= 0) {
    return {
      totalAmount,
      taxableAmount: totalAmount,
      taxAmount: 0,
      taxRate: 0,
      taxName
    }
  }

  // For inclusive tax: Tax Amount = (Total × Tax Rate) / (1 + Tax Rate)
  const taxAmount = (totalAmount * taxRate) / (1 + taxRate)
  const taxableAmount = totalAmount - taxAmount

  return {
    totalAmount: Number(totalAmount.toFixed(2)),
    taxableAmount: Number(taxableAmount.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
    taxRate,
    taxName
  }
}

/**
 * Calculate exclusive tax breakdown
 * For exclusive tax, tax is added on top of the base amount
 * Formula: Tax Amount = Taxable Amount × Tax Rate
 * Formula: Total Amount = Taxable Amount + Tax Amount
 * 
 * @param taxableAmount - The base amount before tax
 * @param taxRate - Tax rate as decimal (e.g., 0.18 for 18%)
 * @param taxName - Name of the tax (e.g., 'GST', 'VAT')
 * @returns Tax calculation breakdown
 */
export function calculateExclusiveTax(
  taxableAmount: number, 
  taxRate: number, 
  taxName: string = 'Tax'
): TaxCalculation {
  if (taxRate <= 0) {
    return {
      totalAmount: taxableAmount,
      taxableAmount,
      taxAmount: 0,
      taxRate: 0,
      taxName
    }
  }

  const taxAmount = taxableAmount * taxRate
  const totalAmount = taxableAmount + taxAmount

  return {
    totalAmount: Number(totalAmount.toFixed(2)),
    taxableAmount: Number(taxableAmount.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
    taxRate,
    taxName
  }
}

/**
 * Calculate tax for cart/order items with inclusive pricing
 * @param subtotal - Subtotal of all items (including tax)
 * @param discount - Discount amount to be applied
 * @param taxSettings - Tax configuration from store settings
 * @returns Tax calculation for the order
 */
export function calculateOrderTax(
  subtotal: number,
  discount: number = 0,
  taxSettings: { enabled: boolean; rate: number; name: string } = { enabled: true, rate: 0.18, name: 'Tax' }
): TaxCalculation {
  if (!taxSettings.enabled) {
    return {
      totalAmount: subtotal - discount,
      taxableAmount: subtotal - discount,
      taxAmount: 0,
      taxRate: 0,
      taxName: taxSettings.name
    }
  }

  // Apply discount first, then calculate tax on the discounted amount
  const discountedAmount = subtotal - discount
  
  return calculateInclusiveTax(discountedAmount, taxSettings.rate, taxSettings.name)
}

/**
 * Calculate optimal grid columns based on item count
 * @param itemCount - Number of items to display
 * @returns Tailwind CSS grid classes for responsive layout
 */
export function getGridColumns(itemCount: number): string {
  if (itemCount <= 1) return "grid-cols-1"
  if (itemCount <= 2) return "grid-cols-1 sm:grid-cols-2"
  if (itemCount <= 3) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
  if (itemCount <= 4) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
  if (itemCount <= 5) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
  if (itemCount <= 6) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
  // For more than 6 items, use 6 columns max
  return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
}

/**
 * Format a number as INR currency with Indian Numbering System and 0 decimal places
 */
export function formatINRCurrency(amount: any, currencySymbol: string = '₹'): string {
  const numericAmount = Number(amount || 0);
  if (isNaN(numericAmount)) {
    return `${currencySymbol}0`;
  }
  // Use Indian Numbering System with 0 decimals
  return `${currencySymbol}${numericAmount.toLocaleString('en-IN', { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
}
